import {
  CameraHelper,
  DirectionalLight,
  DirectionalLightHelper,
  HemisphereLight,
  HemisphereLightHelper,
  Light,
  Object3D,
} from 'three';
import {
  DirectionalLight3JS,
  EnvironmentSettings3JS,
  HemisphereLight3JS,
} from '@ess/jg-rule-executor/dist/src/domain/environment-settings-3js';
import { ConfiguratorOptions } from '@ess/jg-rule-executor/dist/src/domain/configurator-options';
import { ViewMode } from '@src/app/model/view-mode';

export class RenderEnvironmentHelper {
  static getEnvSettings(configuratorOptions: ConfiguratorOptions, viewMode: ViewMode) {
    return viewMode === ViewMode.preview
      ? configuratorOptions?.environmentSettingsResultView
      : configuratorOptions?.environmentSettingsEditView;
  }

  /**
   * The default render environment for the configurator, if no custom environment is set.
   */
  static defaultLights(): [Light[], Object3D[]] {
    // Note; always create the helpers after setting all the parameters (somewhere a deep copy of some properties is used)
    const shadowCameraOffset = Math.pow(2, 4); // 32
    const shadowMapSize = Math.pow(2, 12); // 4096

    // General hemisphere light
    const hemisphereLight: HemisphereLight = new HemisphereLight('#ffffff', '#ffffff', 2.2);
    hemisphereLight.position.set(0, 10, 0);

    // Shadow light
    const shadowCastingLight: DirectionalLight = new DirectionalLight('#ffffff', 1.8);
    shadowCastingLight.castShadow = true;
    shadowCastingLight.position.set(-5, 25, 5);
    shadowCastingLight.shadow.bias = -0.0001; // Needed to make shadow not display as many stripes on objects
    shadowCastingLight.shadow.camera.left = -shadowCameraOffset;
    shadowCastingLight.shadow.camera.right = shadowCameraOffset;
    shadowCastingLight.shadow.camera.top = shadowCameraOffset;
    shadowCastingLight.shadow.camera.bottom = -shadowCameraOffset;
    shadowCastingLight.shadow.mapSize.set(shadowMapSize, shadowMapSize);

    // For debugging, helps with view of the camera positions
    // eslint-disable-next-line prefer-const
    let lightHelpers: (HemisphereLightHelper | DirectionalLightHelper | CameraHelper)[] = [];
    // lightHelpers = [
    //   new HemisphereLightHelper(hemisphereLight, 5, new Color(255, 0, 0)),
    //   new DirectionalLightHelper(shadowCastingLight, 5, new Color(0, 255, 0)),
    //   new CameraHelper(shadowCastingLight.shadow.camera)
    // ];
    //
    // // For debugging purposes, adds a UI for setting some settings
    // const lightGui = new GUI();
    //
    // const hemFolder = lightGui.addFolder('Hemisphere light');
    // hemFolder.add(hemisphereLight, 'intensity').name("Intensity");
    // const hemisphereLightParams = {hemisphereLightColor: hemisphereLight.color.getHex()};
    // hemFolder.addColor(hemisphereLightParams, 'hemisphereLightColor').name("Color")
    //   .onChange(value => hemisphereLight.color.set(value));
    // hemFolder.open();
    //
    // const shaFolder = lightGui.addFolder('Shadow light');
    // shaFolder.add(shadowCastingLight, 'intensity').name("Intensity");
    // const shadowLightParams = {shadowLightColor: shadowCastingLight.color.getHex()};
    // shaFolder.addColor(shadowLightParams, 'shadowLightColor').name("Color")
    //   .onChange(value => shadowCastingLight.color.set(value));
    // shaFolder.open();

    return [[hemisphereLight, shadowCastingLight], lightHelpers];
  }

  static lightsFromEnvSettings(envSettings: EnvironmentSettings3JS): [Light[], Object3D[]] {
    if (!(envSettings?.lightsSettings?.length > 0)) {
      return this.defaultLights();
    }
    const lights: Light[] = envSettings.lightsSettings.map((light) => {
      if (light instanceof HemisphereLight3JS) {
        const light3JS = light as HemisphereLight3JS;
        const hemisphereLight = new HemisphereLight(light3JS.skyColor, light3JS.groundColor, light3JS.intensity);
        hemisphereLight.position.copy(light3JS.position);
        return hemisphereLight;
      } else if (light instanceof DirectionalLight3JS) {
        const light3JS = light as DirectionalLight3JS;
        const directionalLight = new DirectionalLight(light3JS.color, light3JS.intensity);
        directionalLight.position.copy(light3JS.position);
        if (light3JS.target) directionalLight.target.position.copy(light3JS.target);
        if (light3JS.shadowSettings) {
          directionalLight.castShadow = true;
          directionalLight.shadow.bias = light3JS.shadowSettings.bias;
          directionalLight.shadow.camera.left = light3JS.shadowSettings.cameraLeft;
          directionalLight.shadow.camera.right = light3JS.shadowSettings.cameraRight;
          directionalLight.shadow.camera.top = light3JS.shadowSettings.cameraTop;
          directionalLight.shadow.camera.bottom = light3JS.shadowSettings.cameraBottom;
          directionalLight.shadow.mapSize.set(light3JS.shadowSettings.mapSize, light3JS.shadowSettings.mapSize);
        }
        return directionalLight;
      } else {
        console.error(`Unknown light type in environment settings, skipping: ${JSON.stringify(light)}`);
      }
    });
    // eslint-disable-next-line prefer-const
    let lightHelpers: (DirectionalLightHelper | HemisphereLightHelper)[] = [];
    // lightHelpers = lights.map(light => {
    //   switch (light.constructor.name) {
    //     case HemisphereLight.name: {
    //       const hemisphereLight = light as HemisphereLight;
    //       return new HemisphereLightHelper(hemisphereLight, 5);
    //     }
    //     case DirectionalLight.name: {
    //       const directionalLight = light as DirectionalLight;
    //       return new DirectionalLightHelper(directionalLight, 5);
    //     }
    //   }
    // });
    return [lights, lightHelpers];
  }

  static getShadowFloorOpacity(envSettings: EnvironmentSettings3JS, withTexture = false): number {
    if (envSettings?.shadowFloorOpacity) {
      return envSettings.shadowFloorOpacity;
    } else if (withTexture) {
      return 0.4;
    }
    return 0.2;
  }
}
